import * as React from 'react';

interface Latin1ValidationContextValue {
    registerError: (id: string, fieldName: string, error: string | null) => void;
    getErrors: () => string[];
}

const Latin1ValidationContext = React.createContext<Latin1ValidationContextValue | undefined>(undefined);

export const Latin1ValidationProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
    // Map of input ID to { fieldName, errorMessage }
    const errorsRef = React.useRef<Map<string, { fieldName: string; errorMessage: string }>>(new Map());
    const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

    const registerError = React.useCallback((id: string, fieldName: string, error: string | null) => {
        if (error) {
            errorsRef.current.set(id, { fieldName, errorMessage: error });
        } else {
            errorsRef.current.delete(id);
        }
        forceUpdate();
    }, []);

    const getErrors = React.useCallback((): string[] => {
        const errors: string[] = [];
        errorsRef.current.forEach(({ fieldName, errorMessage }) => {
            errors.push(`${fieldName}: ${errorMessage}`);
        });
        return errors;
    }, []);

    const value = React.useMemo(
        () => ({
            registerError,
            getErrors,
        }),
        [registerError, getErrors],
    );

    return <Latin1ValidationContext.Provider value={value}>{children}</Latin1ValidationContext.Provider>;
};

export const useLatin1ValidationContext = (): Latin1ValidationContextValue => {
    const context = React.useContext(Latin1ValidationContext);
    if (!context) {
        // Return a no-op implementation if context is not available
        return {
            registerError: () => {},
            getErrors: () => [],
        };
    }
    return context;
};
